import React from "react";
import "./burndown-chart.scss";
import { BurndownPoint, SprintBurndown } from "./types";

export interface BurndownChartProps {
  burndown: SprintBurndown | null;
  loading: boolean;
  // Compact mode (backlog sprint header): smaller, no legend.
  compact?: boolean;
}

// Locale-stable day label (CLAUDE.md: pin the culture so output is identical everywhere).
const formatDay = (iso: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Sprint burndown as an SVG: the ideal line (dashed, burns down on working days only), the actual
// remaining line (solid, stops at today), and — only when work was added/removed mid-sprint — a
// faint scope line so the change is visible. The actual line can rise above the ideal when scope grows.
const BurndownChart: React.FC<BurndownChartProps> = ({ burndown, loading, compact = false }) => {
  if (loading) {
    return <div className="kuika-burndown__empty">Loading…</div>;
  }
  if (!burndown || (burndown.totalScope === 0 && burndown.committedScope === 0) || burndown.points.length === 0) {
    return <div className="kuika-burndown__empty">No scope in this sprint yet.</div>;
  }
  const pts: BurndownPoint[] = burndown.points;
  const width: number = 100;
  const height: number = 40;
  const maxY: number = Math.max(
    1,
    burndown.committedScope,
    burndown.totalScope,
    ...pts.map((p: BurndownPoint) => p.scope)
  );
  const step: number = pts.length > 1 ? width / (pts.length - 1) : width;
  const y = (value: number): number => height - (value / maxY) * height;
  const idealLine: string = pts
    .map((p: BurndownPoint, i: number) => `${(i * step).toFixed(2)},${y(p.ideal).toFixed(2)}`)
    .join(" ");
  const scopeLine: string = pts
    .map((p: BurndownPoint, i: number) => `${(i * step).toFixed(2)},${y(p.scope).toFixed(2)}`)
    .join(" ");
  const actualLine: string = pts
    .map((p: BurndownPoint, i: number) =>
      p.remaining == null ? null : `${(i * step).toFixed(2)},${y(p.remaining).toFixed(2)}`
    )
    .filter((point): point is string => point !== null)
    .join(" ");
  const scopeChanged: boolean = pts.some((p: BurndownPoint) => Math.abs(p.scope - burndown.committedScope) > 0.0001);

  return (
    <div className={`kuika-burndown${compact ? " kuika-burndown--compact" : ""}`}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="kuika-burndown__svg">
        {scopeChanged && (
          <polyline
            points={scopeLine}
            fill="none"
            stroke="var(--dark-4)"
            strokeWidth="0.6"
            strokeDasharray="1 1.5"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <polyline
          points={idealLine}
          fill="none"
          stroke="var(--dark-5)"
          strokeWidth="0.7"
          strokeDasharray="2 2"
          vectorEffect="non-scaling-stroke"
        />
        <polyline
          points={actualLine}
          fill="none"
          stroke="var(--blue-5)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="kuika-burndown__axis">
        <span>{formatDay(burndown.startDate)}</span>
        <span>{formatDay(burndown.endDate)}</span>
      </div>
      {!compact && (
        <div className="kuika-burndown__legend">
          <span className="kuika-burndown__legend-item">
            <span className="kuika-burndown__legend-dot" style={{ background: "var(--blue-5)" }} />
            Remaining<strong>{burndown.remainingToday}</strong>
          </span>
          <span className="kuika-burndown__legend-item">
            <span className="kuika-burndown__legend-dash" />
            Ideal
          </span>
          {scopeChanged && (
            <span className="kuika-burndown__legend-item">
              <span className="kuika-burndown__legend-dash kuika-burndown__legend-dash--scope" />
              Scope
            </span>
          )}
          <span className="kuika-burndown__legend-item kuika-burndown__legend-item--muted">
            {burndown.unit === "points" ? "Story points" : "Issue count"} · scope {burndown.totalScope}
          </span>
        </div>
      )}
    </div>
  );
};

export default BurndownChart;
